import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Select, DatePicker, theme, Spin } from 'antd';
import { message } from '../services/toastMessage';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { IconCurrentLocation, IconCalendarEvent, IconX, IconChevronRight, IconArrowLeft } from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import LocationAutocomplete from './LocationAutocomplete';
import HomeSearchLocationScreen from './HomeSearchLocationScreen';
import HomeSearchWhenScreen from './HomeSearchWhenScreen';
import HomeSearchCategoryScreen from './HomeSearchCategoryScreen';
import { IBookingRange } from './AvailabilityCalendar';
import { ILocation } from '../interfaces/heavyDutyMachinery/ILocation';
import { RENTAL_MODE_SEARCH_OPTIONS } from '../constants/heavyDutyMachinerySpaceOptions';
import { RADIUS_MAX } from './FiltersModal';
import { reverseGeocode } from '../services/MapsService';
import { resolveServiceCategoryName } from '../helpers/serviceCategoryTaxonomy';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { useAntdPickerLocale } from '../hooks/useAntdPickerLocale';
import { useTaxonomy } from '../hooks/useTaxonomy';
import { useAccessoryTaxonomy } from '../hooks/useAccessoryTaxonomy';
import { CORAL } from '../theme/creamyTokens';

const { RangePicker } = DatePicker;

// Default radius for any search anchored to a real point (near me, or a
// geocoded suggestion) -- deliberately narrower than FiltersModal's own
// "no filter" ceiling (RADIUS_MAX), since resolving an actual point implies
// the user wants what's nearby, not everything in the region/country a
// bare city name might otherwise text-match against (see searchHeavyDutyMachineries:
// city is only ever sent as a filter when there's no lat/lng to search by
// radius with instead). Same radiusKm field/concept either way (clamped
// against the same ceiling), just this shared starting value.
const LOCATION_DEFAULT_RADIUS_KM = Math.min(25, RADIUS_MAX);

export interface HomeSearchState {
  city?: string;
  lat?: number;
  lng?: number;
  // Set whenever lat/lng are (near me, or a geocoded suggestion) -- see
  // LOCATION_DEFAULT_RADIUS_KM above. Left unset when there's no point to
  // search around, same as FiltersModal's own radius slider being disabled
  // without one.
  radiusKm?: number;
  rentalMode?: string;
  // A MachineryType.Code (see ITaxonomy.ts), e.g. "excavadora-de-cadenas" --
  // same single-value convention as rentalMode above, not the array shape
  // FiltersModal's own multi-select category facet uses. In 'accessories'
  // mode this holds an AccessoryType.Code instead (see
  // IAccessoryTaxonomy.ts) -- a different taxonomy, same single-value shape.
  category?: string;
  dateRange?: [Dayjs, Dayjs];
  // Only set (and only ever read) when rentalMode is "Hourly" -- a real-time
  // availability check resolved server-side by
  // AvailabilityCalculator.GetNextAvailableSlot. "Now": availabilityAt is
  // ignored, the search runs from the real current instant. "At":
  // availabilityAt carries the exact moment -- "In 2h"/"In 3h" (resolved in
  // this component) and a hand-picked custom date+time both end up as this
  // same shape, there's no separate "quick preset" concept past this point.
  availabilityMode?: 'Now' | 'At';
  availabilityAt?: Dayjs;
}

// Exported for HomeSearchWhenScreen's own mobile quick-availability chips
// (see the isMobile branch below), which reuse this same set (minus
// 'custom' -- mobile picks a specific date/time via its own full calendar
// screen instead of the hidden-DatePicker trick desktop's 4th chip uses).
export type AvailabilityPreset = 'now' | '+2h' | '+3h' | 'custom';
// The mobile overlay's own internal navigation -- 'main' is the 4-row
// screen (Tipo de maquinaria/Where/Rental mode/When), 'category'/'where'/
// 'when' are full-screen subscreens pushed on top of it (see the isMobile
// branch's own "screen" state and its AnimatePresence slide transition).
type OverlayScreen = 'main' | 'where' | 'when' | 'category';

const AVAILABILITY_OPTIONS: { value: AvailabilityPreset; labelKey: string }[] = [
  { value: 'now', labelKey: TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_NOW },
  { value: '+2h', labelKey: TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_PLUS_2H },
  { value: '+3h', labelKey: TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_PLUS_3H },
  { value: 'custom', labelKey: TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_CUSTOM },
];

interface HomeSearchBarProps {
  // Prefills the bar -- used by /explore to reflect the filters already
  // active (e.g. from a shared/bookmarked URL) instead of starting blank.
  initialValues?: HomeSearchState;
  // When set, "Search" calls this instead of navigating to /heavy-duty-machinery --
  // /explore is already on that page, it just wants the same bar's values
  // folded into its own filter state.
  onSearch?: (values: HomeSearchState) => void;
  style?: React.CSSProperties;
  // Opt-in, isMobile-only one-time attention pulse on the collapsed search
  // control (see index.css's .search-control) -- only the actual home
  // screen (Landing) passes this, so re-searching from /heavy-duty-machinery's own
  // results page doesn't replay it on every mount.
  pulseOnMount?: boolean;
  // Both maps reuse this exact same bar for their Accessories/Operators/
  // Services category tabs (see MapCategoryPicker) -- same visual control and
  // overlay, just narrowed to the two fields that mean anything for those
  // three ("Tipo de X" + "Dónde"): none of them has a rental modality, and
  // none has a date-based availability check the way Machinery does, so those
  // two rows/sections are dropped entirely rather than shown non-functional.
  //
  // Services is the one whose "Tipo de" catalogue is NOT fetched: it is a
  // curated list on this side (see buildServiceCategoryTaxonomy), shaped like
  // the other two taxonomies so the subscreen needs no second code path.
  mode?: 'machinery' | 'operators' | 'accessories' | 'services';
}

interface SearchNavRowProps {
  label: string;
  summary: string;
  hasSummary: boolean;
  onClick: () => void;
}

/** One tappable pill-row in the mobile overlay's main screen -- see
 * HomeSearchBar's isMobile branch. "Where" and "When" each navigate to
 * their own full-screen subscreen (see the "screen" state) rather than
 * expanding inline, since both need real room (a location search list, a
 * whole calendar) that a narrow accordion card can't give them. */
const SearchNavRow: React.FC<SearchNavRowProps> = ({ label, summary, hasSummary, onClick }) => {
  const { token } = theme.useToken();
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '16px 18px',
        marginBottom: 12,
        borderRadius: 20,
        border: 'none',
        // Plain neutral gray fill (same as Landing's own pill cards), not the
        // "latte" SURFACE_WARM/SURFACE_1 tone -- explicitly reverted per
        // feedback, same as the rest of the app's pill cards already were.
        background: token.colorFillAlter,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span>
        <span style={{ display: 'block', fontSize: 13, color: token.colorTextSecondary }}>{label}</span>
        <span
          style={{
            display: 'block',
            fontSize: 16,
            fontWeight: hasSummary ? 600 : 400,
            marginTop: 2,
            color: hasSummary ? token.colorText : token.colorTextTertiary,
          }}
        >
          {summary}
        </span>
      </span>
      <IconChevronRight size={18} style={{ flexShrink: 0, color: token.colorTextSecondary }} />
    </button>
  );
};

/**
 * The landing page's own search bar -- a single continuous pill (Airbnb's
 * own search bar shape) with three sections (Where/When/Rental mode)
 * separated by thin dividers, then a circular search button. Hands off to
 * /heavy-duty-machinery's own (already-built) faceted filter system via query params,
 * same shape it already reads from a shared/bookmarked URL.
 *
 * "When" means two different things depending on rental mode: Overnight
 * keeps a plain check-in/check-out range (day-level only, no time of day).
 * Hourly swaps that for Now/In 2h/In 3h/Pick-a-date shortcuts, since
 * "available" there means a real time-of-day slot, not just a day -- see
 * AvailabilityMode/AvailabilityAt on IHeavyDutyMachinerySearchFilters.
 *
 * Also reused as-is (same component, no duplication) as /explore's own top
 * search bar via the onSearch/initialValues props above.
 *
 * On isMobile this collapses to a single minimal control (icon + a summary
 * of whatever's already applied, or a placeholder) that opens a full-screen
 * overlay with the three fields as an accordion -- editing there is a draft
 * (see draftSearch/draftWhenRange below) that only replaces the real
 * search/whenRange state once "Search" is pressed; "x" discards it. Its own
 * "When" card uses the real AvailabilityCalendar/HourlyRangeTimeline (see
 * renderWhenCalendar) instead of renderWhenField's chips/RangePicker, which
 * stay exactly as they were for desktop/tablet's own continuous pill below.
 */
const HomeSearchBar: React.FC<HomeSearchBarProps> = ({ initialValues, onSearch, style, pulseOnMount, mode = 'machinery' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pickerLocale = useAntdPickerLocale();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  // Accessories mode searches its own AccessoryType taxonomy (what an
  // accessory IS -- martillo hidráulico, cazo...), not Machinery's Sector/
  // MachineryFamily/MachineryType taxonomy every other mode uses -- see
  // HomeSearchCategoryScreen's own `taxonomySource` doc comment. Both hooks
  // are always called (rules of hooks), but useAccessoryTaxonomy's own
  // `enabled` skips its fetch outside accessories mode.
  const { resolveName: resolveMachineryCategoryName } = useTaxonomy();
  const { resolveName: resolveAccessoryTypeName } = useAccessoryTaxonomy(mode === 'accessories');
  // Services are not a fetched taxonomy at all -- a curated list on this side
  // (see buildServiceCategoryTaxonomy), so their names resolve synchronously.
  const resolveCategoryName =
    mode === 'accessories'
      ? resolveAccessoryTypeName
      : mode === 'services'
        ? (slug: string) => resolveServiceCategoryName(t, slug)
        : resolveMachineryCategoryName;
  const [search, setSearch] = useState<HomeSearchState>(initialValues ?? {});

  // Restoring from a URL (initialValues) can't tell "+2h" apart from a
  // hand-picked time once it's already an absolute timestamp -- both just
  // become "custom" showing that same timestamp, which is an honest
  // representation either way. Defaults to "now" otherwise, so a guest who
  // never touches this control still gets the single-tap "I want it now"
  // flow the whole feature is for.
  const [preset, setPreset] = useState<AvailabilityPreset>(
    initialValues?.availabilityMode === 'At' ? 'custom' : 'now'
  );
  const [customAt, setCustomAt] = useState<Dayjs | undefined>(
    initialValues?.availabilityMode === 'At' ? initialValues.availabilityAt : undefined
  );

  // initialValues is derived from the page's own filters state (see
  // HeavyDutyMachineries' searchBarInitialValues, memoized so its reference is
  // stable unless something in it genuinely changed) and can change from
  // OUTSIDE this component entirely -- e.g. FiltersModal's "Limpiar
  // filtros" resetting filters.city to undefined left this component still
  // showing the old "Valle de Mena" (the mobile trigger button's own
  // summary and the desktop input both read `search` directly), since the
  // useState initializers above only ever run once, at mount. Re-syncing
  // here whenever initialValues' reference changes keeps this component
  // honest about state it doesn't actually own.
  useEffect(() => {
    setSearch(initialValues ?? {});
    setPreset(initialValues?.availabilityMode === 'At' ? 'custom' : 'now');
    setCustomAt(initialValues?.availabilityMode === 'At' ? initialValues.availabilityAt : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // The date+time picker has no visible input box of its own (see the
  // hidden DatePicker below) -- clicking "Pick a date" opens it as a
  // popover anchored to this section instead of an inline field.
  const [pickerOpen, setPickerOpen] = useState(false);

  const [locating, setLocating] = useState(false);

  // Mobile-only draft state -- see the component's own doc comment above.
  // Seeded from the real state when the overlay opens (openOverlay below)
  // and discarded (never copied back) if the user taps "x" instead of
  // "Search".
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [screen, setScreen] = useState<OverlayScreen>('main');
  const [draftSearch, setDraftSearch] = useState<HomeSearchState>(search);
  // The mobile overlay's own "When" subscreen (HomeSearchWhenScreen) reuses
  // AvailabilityCalendar/HourlyRangeTimeline directly rather than the preset
  // chips, so it needs the raw {startAt, endAt} range (for its own summary
  // text) alongside whatever draftSearch fields that maps to. whenRange
  // mirrors search the same way draftWhenRange mirrors draftSearch.
  const [whenRange, setWhenRange] = useState<IBookingRange | undefined>(undefined);
  const [draftWhenRange, setDraftWhenRange] = useState<IBookingRange | undefined>(whenRange);

  const openOverlay = () => {
    setDraftSearch(search);
    setDraftWhenRange(whenRange);
    setScreen('main');
    setOverlayOpen(true);
  };

  // Resolved at search time, not at the moment a chip is clicked -- picking
  // "In 2 hours" and waiting five minutes before hitting Search should mean
  // "2h from when I actually searched", not "2h from when I clicked it".
  // Takes the preset/custom-time explicitly (rather than always reading the
  // top-level state) so the mobile overlay's own Search button can resolve
  // against its still-uncommitted draft instead.
  const resolveAvailability = (
    presetArg: AvailabilityPreset,
    customAtArg: Dayjs | undefined
  ): Pick<HomeSearchState, 'availabilityMode' | 'availabilityAt'> => {
    switch (presetArg) {
      case 'now':
        return { availabilityMode: 'Now', availabilityAt: undefined };
      case '+2h':
        return { availabilityMode: 'At', availabilityAt: dayjs().add(2, 'hour') };
      case '+3h':
        return { availabilityMode: 'At', availabilityAt: dayjs().add(3, 'hour') };
      case 'custom':
        return { availabilityMode: 'At', availabilityAt: customAtArg };
      default:
        return {};
    }
  };

  // Shared by the Search button(s) and "near me" (which can't just update
  // `search` state and call handleSearch -- state updates aren't visible
  // until the next render, and near me needs to submit the coordinates it
  // just resolved, not whatever was in state before them). Defaults to the
  // top-level preset/customAt so every existing (desktop, non-overlay)
  // caller is unaffected; the mobile overlay's Search button passes its own
  // draft values explicitly instead. skipResolve is for the mobile overlay's
  // own "When" calendar (renderWhenCalendar below), which already writes a
  // fully-resolved availabilityMode/availabilityAt straight onto draftSearch
  // itself -- applying resolveAvailability on top of that would overwrite it
  // with the unrelated (and by then stale) preset/customAt chip state.
  const submitSearch = (
    base: HomeSearchState,
    presetArg: AvailabilityPreset = preset,
    customAtArg: Dayjs | undefined = customAt,
    skipResolve = false
  ) => {
    const hourly = base.rentalMode === 'Hourly';
    const resolved: HomeSearchState = hourly && !skipResolve ? { ...base, ...resolveAvailability(presetArg, customAtArg) } : base;

    if (onSearch) {
      onSearch(resolved);
      return;
    }
    const params = new URLSearchParams();
    if (resolved.city) params.set('city', resolved.city);
    if (resolved.lat != null) params.set('lat', String(resolved.lat));
    if (resolved.lng != null) params.set('lng', String(resolved.lng));
    if (resolved.radiusKm != null) params.set('radiusKm', String(resolved.radiusKm));
    if (resolved.rentalMode) params.set('rentalMode', resolved.rentalMode);
    if (resolved.category) params.set('category', resolved.category);
    if (hourly) {
      if (resolved.availabilityMode) params.set('availabilityMode', resolved.availabilityMode);
      if (resolved.availabilityMode === 'At' && resolved.availabilityAt) {
        params.set('availabilityAt', resolved.availabilityAt.toISOString());
      }
    } else if (resolved.dateRange) {
      params.set('startDate', resolved.dateRange[0].format('YYYY-MM-DD'));
      params.set('endDate', resolved.dateRange[1].format('YYYY-MM-DD'));
    }
    navigate(`/heavy-duty-machinery${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleSearch = () => submitSearch(search);

  const handleOverlaySearch = () => {
    setSearch(draftSearch);
    setWhenRange(draftWhenRange);
    setOverlayOpen(false);
    submitSearch(draftSearch, preset, customAt, true);
  };

  const handleClearAll = () => {
    setDraftSearch({});
    setDraftWhenRange(undefined);
  };

  // Shared by both the desktop bar's own "near me" and the mobile overlay's
  // -- only the target state, whether finding a point should submit
  // immediately (which only makes sense outside the overlay's draft/review
  // step), and what else to do once resolved (the mobile "Where" subscreen
  // navigates back to the main screen, same as picking a suggestion there)
  // differ.
  const createNearMeHandler = (
    currentValue: HomeSearchState,
    setValue: React.Dispatch<React.SetStateAction<HomeSearchState>>,
    autoSubmit: boolean,
    onResolved?: () => void
  ) => () => {
    if (!navigator.geolocation) {
      message.error(translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_NEAR_ME_UNSUPPORTED));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const placeName = await reverseGeocode(lat, lng).catch(() => undefined);
        const city = placeName || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        const updated: HomeSearchState = { ...currentValue, city, lat, lng, radiusKm: LOCATION_DEFAULT_RADIUS_KM };
        setLocating(false);
        setValue(updated);
        onResolved?.();
        if (autoSubmit) submitSearch(updated);
      },
      () => {
        setLocating(false);
        message.error(translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_NEAR_ME_DENIED));
      },
      { timeout: 10000 }
    );
  };

  const handleNearMe = createNearMeHandler(search, setSearch, true);
  const handleDraftNearMe = createNearMeHandler(draftSearch, setDraftSearch, false, () => setScreen('main'));

  const microLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: token.colorTextTertiary,
    marginBottom: 2,
    display: 'block',
  };

  const sectionStyle = (isLast: boolean): React.CSSProperties => ({
    flex: 1,
    minWidth: 0,
    padding: isMobile ? '10px 20px' : '8px 20px',
    borderRight: !isMobile && !isLast ? `1px solid ${token.colorBorderSecondary}` : undefined,
    borderBottom: isMobile && !isLast ? `1px solid ${token.colorBorderSecondary}` : undefined,
    position: 'relative',
  });

  // Shared between every layout below (desktop's continuous divided pill
  // and the mobile overlay's accordion) -- each takes an explicit
  // value/setter pair instead of closing over top-level state directly, so
  // the exact same markup/behavior can be bound to either the committed
  // `search` state (desktop) or the mobile overlay's still-uncommitted
  // draft, with only one copy of the logic to keep in sync.
  const renderWhereField = (
    value: HomeSearchState,
    setValue: React.Dispatch<React.SetStateAction<HomeSearchState>>,
    onNearMe: () => void
  ) => (
    <div style={{ position: 'relative' }}>
      <LocationAutocomplete
        value={value.city ?? ''}
        onChange={(city) => setValue((prev) => ({ ...prev, city: city || undefined, lat: undefined, lng: undefined, radiusKm: undefined }))}
        onSelectLocation={(location: ILocation) => {
          // Same convention as /heavy-duty-machinery's own filter bar: a province/region/
          // country-level suggestion (or a geocoding miss) comes back as
          // exactly (0, 0).
          const hasPoint = location.latitude !== 0 || location.longitude !== 0;
          setValue((prev) => ({
            ...prev,
            city: location.locality || location.formattedAddress,
            lat: hasPoint ? location.latitude : undefined,
            lng: hasPoint ? location.longitude : undefined,
            radiusKm: hasPoint ? LOCATION_DEFAULT_RADIUS_KM : undefined,
          }));
        }}
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CITY_PLACEHOLDER)}
        allowClear
        useOwnData
        commitOnSelectOnly
        variant="borderless"
        style={{ width: '100%', padding: 0, paddingRight: 44 }}
      />
      {/* Icon stays the same visual size (18px) -- padding grows the tap
          target itself to the 44px minimum instead, so the touch area is
          comfortable without the button looking oversized next to the
          input text. */}
      <button
        type="button"
        aria-label={translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_NEAR_ME_LABEL)}
        onClick={onNearMe}
        disabled={locating}
        style={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          border: 'none',
          background: 'none',
          padding: 13,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: locating ? 'default' : 'pointer',
          color: token.colorTextSecondary,
        }}
      >
        {locating ? <Spin size="small" /> : <IconCurrentLocation size={18} />}
      </button>
    </div>
  );

  const renderWhenField = (
    value: HomeSearchState,
    setValue: React.Dispatch<React.SetStateAction<HomeSearchState>>,
    presetValue: AvailabilityPreset,
    setPresetValue: React.Dispatch<React.SetStateAction<AvailabilityPreset>>,
    customAtValue: Dayjs | undefined,
    setCustomAtValue: React.Dispatch<React.SetStateAction<Dayjs | undefined>>,
    pickerOpenValue: boolean,
    setPickerOpenValue: React.Dispatch<React.SetStateAction<boolean>>
  ) =>
    value.rentalMode === 'Hourly' ? (
      <>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 5 }}>
          {AVAILABILITY_OPTIONS.map((opt) => (
            <div
              key={opt.value}
              onClick={() => {
                setPresetValue(opt.value);
                if (opt.value === 'custom') setPickerOpenValue(true);
              }}
              style={{
                cursor: 'pointer',
                position: 'relative',
                padding: '6px 12px',
                fontSize: 14,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                color: presetValue === opt.value ? token.colorText : token.colorTextTertiary,
                fontWeight: presetValue === opt.value ? 600 : 400,
                transition: 'color 150ms ease-out',
              }}
            >
              {presetValue === opt.value && (
                <motion.div
                  layoutId="availability-bubble"
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 999,
                    background: token.colorBgContainer,
                    border: '1px solid rgba(0,0,0,0.12)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.14)',
                  }}
                />
              )}
              <span style={{ position: 'relative' }}>{translate(t, opt.labelKey)}</span>
            </div>
          ))}
        </div>
        {/* Once a custom date/time is confirmed (via the picker's own
            Apply/Ok button below), this shows *what* was picked and offers
            its own way to clear it -- absolutely positioned (instead of
            just another item in the row above) straddling the section's
            own bottom edge, so a long formatted date never wraps the chips
            row onto a second line and grows the whole section's height.
            "Pick a date" above stays selectable and keeps showing the
            shared bubble like any other option; this is purely an
            on-top-of-everything summary of what it currently holds -- and
            only while it's actually the active choice, so switching to
            Now/+2h/+3h drops it instead of leaving it dangling as if still
            selected. */}
        <AnimatePresence>
          {presetValue === 'custom' && customAtValue && (
            <motion.div
              key="custom-availability-pill"
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              onClick={() => {
                setPresetValue('custom');
                setPickerOpenValue(true);
              }}
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                // motion's own x/y (not a plain `transform` string, which it
                // would just overwrite with its own scale animation) --
                // centers this on the section both horizontally and on its
                // own bottom edge.
                x: '-50%',
                y: '-50%',
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                cursor: 'pointer',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                fontSize: 13,
                fontWeight: 600,
                color: token.colorText,
                background: token.colorBgContainer,
                border: '1px solid rgba(0,0,0,0.12)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.14)',
                borderRadius: 999,
                padding: '6px 8px 6px 14px',
              }}
            >
              <IconCalendarEvent size={14} style={{ flexShrink: 0 }} />
              <span>{customAtValue.format('D MMM, HH:mm')}</span>
              <button
                type="button"
                aria-label={translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_CLEAR)}
                onClick={(e) => {
                  // Otherwise this bubbles up to the pill's own onClick above
                  // and reopens the picker right as it's being cleared.
                  e.stopPropagation();
                  setPresetValue('now');
                  setCustomAtValue(undefined);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: 'none',
                  background: 'rgba(0,0,0,0.06)',
                  color: 'inherit',
                  borderRadius: '50%',
                  width: 18,
                  height: 18,
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <IconX size={11} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        {/* No visible input box of its own -- opened/closed entirely via the
            "Pick a date" shortcut above, its dropdown panel still anchors to
            this section since that's where this hidden trigger sits in the
            layout. Sized to actually cover the section (not a 0x0 point) so
            the popup's own placement math has a real box to align against --
            pointer-events is off since opening/closing is only ever driven
            by our own click handlers above, never by this element being
            clicked directly. */}
        <DatePicker
          showTime
          open={pickerOpenValue}
          onOpenChange={setPickerOpenValue}
          value={customAtValue}
          onChange={(date) => setCustomAtValue(date ?? undefined)}
          disabledDate={(date) => date.isBefore(dayjs().startOf('day'))}
          format="DD/MM/YYYY HH:mm"
          suffixIcon={null}
          locale={pickerLocale}
          popupClassName="heavyDutyMachinery-availability-picker-popup"
          style={{ position: 'absolute', inset: 0, padding: 0, border: 'none', opacity: 0, pointerEvents: 'none' }}
        />
      </>
    ) : (
      <RangePicker
        variant="borderless"
        className="heavyDutyMachinery-range-input"
        popupClassName="heavyDutyMachinery-availability-picker-popup"
        locale={pickerLocale}
        style={{ width: '100%', padding: 0 }}
        value={value.dateRange}
        onChange={(dates) =>
          setValue((prev) => ({
            ...prev,
            dateRange: dates && dates[0] && dates[1] ? [dates[0], dates[1]] : undefined,
          }))
        }
        disabledDate={(date) => date.isBefore(dayjs().startOf('day'))}
        placeholder={[
          translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_DATE_START_PLACEHOLDER),
          translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_DATE_END_PLACEHOLDER),
        ]}
      />
    );

  // Feeds the mobile overlay's own "When" subscreen (HomeSearchWhenScreen,
  // see the isMobile branch below) -- there's no specific space yet at
  // search time, but the guest should still pick a date by tapping days on
  // an actual calendar, same as the booking flow, rather than typing into a
  // plain field. Desktop's own renderWhenField (chips/RangePicker) is
  // untouched. Also used for both the subscreen's own collapsed-summary and
  // the outer pill control's summary -- reflects the actual {startAt, endAt}
  // range rather than a generic label, per rentalMode's own display
  // convention (a single day for Overnight/DayPass, day + time-of-day for
  // Hourly).
  const handleWhenSelectionChange = (range: IBookingRange | undefined) => {
    setDraftWhenRange(range);
    if (!range) {
      setDraftSearch((prev) => ({ ...prev, dateRange: undefined, availabilityMode: undefined, availabilityAt: undefined }));
      return;
    }
    // A manual pick isn't one of the quick presets below -- 'custom' (an
    // existing AvailabilityPreset value, not a real 4th chip on mobile)
    // just means "none of Now/+2h/+3h", so tapping back into the calendar
    // after a quick pick correctly drops that chip's highlight.
    setPreset('custom');
    if (draftSearch.rentalMode === 'Hourly') {
      setDraftSearch((prev) => ({ ...prev, dateRange: undefined, availabilityMode: 'At', availabilityAt: dayjs(range.startAt) }));
    } else {
      setDraftSearch((prev) => ({ ...prev, availabilityMode: undefined, availabilityAt: undefined, dateRange: [dayjs(range.startAt), dayjs(range.endAt)] }));
    }
  };

  // Mobile's own quick-availability chips (HomeSearchWhenScreen) -- same
  // three options ('custom' excluded, see AvailabilityPreset's own comment)
  // as desktop's renderWhenField, but resolved immediately into draftSearch
  // rather than deferred to submit time: unlike desktop's single bar, this
  // screen also offers a full calendar/timeline for a manual pick, so
  // there's no single later "submit" moment to resolve a still-pending
  // preset against -- same reasoning handleWhenSelectionChange above
  // already resolves immediately for a calendar pick.
  const handleQuickAvailability = (presetArg: 'now' | '+2h' | '+3h') => {
    setPreset(presetArg);
    setDraftWhenRange(undefined);
    setDraftSearch((prev) => ({ ...prev, dateRange: undefined, ...resolveAvailability(presetArg, customAt) }));
  };

  // Split date/time, for the mobile collapsed pill's own two-line summary
  // (place on top, date+time below it).
  const formatWhenDate = (range: IBookingRange | undefined): string | undefined => {
    if (!range) return undefined;
    const start = dayjs(range.startAt);
    const end = dayjs(range.endAt);
    return start.isSame(end, 'day') ? start.format('D MMM') : `${start.format('D MMM')} – ${end.format('D MMM')}`;
  };
  const formatWhenTime = (mode: HomeSearchState['rentalMode'], range: IBookingRange | undefined): string | undefined =>
    range && mode === 'Hourly' ? `${dayjs(range.startAt).format('HH:mm')}–${dayjs(range.endAt).format('HH:mm')}` : undefined;

  // Layers the mobile-only quick-availability presets (Now/+2h/+3h -- see
  // HomeSearchWhenScreen) on top of the two formatters above, for whichever
  // of the two actually produced this selection: a manual calendar/timeline
  // pick always comes with a real `range`; a quick preset has none -- there
  // is no client-known specific slot for "now" or "in 2h" (see
  // resolveAvailability), only whatever each result's own search actually
  // finds (shown per-space as HeavyDutyMachineryCard's nextAvailableSlot) -- so
  // it's read directly off availabilityMode/availabilityAt instead. Used
  // for both the collapsed pill and the overlay's own "When" row summary.
  const describeAvailability = (value: HomeSearchState, range: IBookingRange | undefined): { date?: string; time?: string } => {
    if (range) return { date: formatWhenDate(range), time: formatWhenTime(value.rentalMode, range) };
    if (value.rentalMode !== 'Hourly') return {};
    if (value.availabilityMode === 'Now') return { date: translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_NOW) };
    if (value.availabilityMode === 'At' && value.availabilityAt) {
      return { date: value.availabilityAt.format('D MMM'), time: value.availabilityAt.format('HH:mm') };
    }
    return {};
  };

  const renderModeField = (value: HomeSearchState, setValue: React.Dispatch<React.SetStateAction<HomeSearchState>>) => (
    <Select
      allowClear
      variant="borderless"
      placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_RENTAL_MODE_PLACEHOLDER)}
      style={{ width: '100%' }}
      value={value.rentalMode}
      onChange={(mode) => setValue((prev) => ({ ...prev, rentalMode: mode, dateRange: undefined }))}
      options={RENTAL_MODE_SEARCH_OPTIONS.map((o) => ({ label: translate(t, o.labelKey), value: o.value }))}
    />
  );

  // Rental mode's overlay variant is chips (Hourly/Overnight/Day pass) rather
  // than modeField's Select -- explicitly asked for in that layout, and a
  // better fit for a big touch-friendly panel anyway. No "Any" option here
  // (unlike modeField's clearable Select): the "When" subscreen's own picker
  // shape depends entirely on which of the three is picked, so leaving it
  // unset would leave that subscreen not knowing what to show.
  const renderModeChips = (value: HomeSearchState, setValue: React.Dispatch<React.SetStateAction<HomeSearchState>>) => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {RENTAL_MODE_SEARCH_OPTIONS.map((opt) => {
        const selected = (value.rentalMode ?? undefined) === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => setValue((prev) => ({ ...prev, rentalMode: opt.value, dateRange: opt.value !== prev.rentalMode ? undefined : prev.dateRange }))}
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              border: `1.5px solid ${selected ? CORAL : token.colorBorderSecondary}`,
              background: selected ? CORAL : token.colorBgContainer,
              color: selected ? '#fff' : token.colorText,
              fontWeight: selected ? 600 : 400,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {translate(t, opt.labelKey)}
          </button>
        );
      })}
    </div>
  );

  // Mobile gets its own layout entirely: a minimal pill control showing
  // either a placeholder or a summary of whatever's already applied, and a
  // full-screen overlay (opened on tap). That overlay is itself a tiny
  // 2-level navigation stack (see the "screen" state): its main screen has
  // three rows -- Where, Rental mode, When, in that order since rental mode
  // determines what kind of picker "When" needs (a day-range vs. a day +
  // hourly timeline) -- Where/When each push a dedicated full-screen
  // subscreen (HomeSearchLocationScreen/HomeSearchWhenScreen) rather than
  // expanding inline, since both need real room a narrow accordion card
  // can't give them. Rental mode stays inline chips (just 3 options, no
  // subscreen needed). One shared header (back, conditional on not being on
  // the main screen + close) covers all three "screens" instead of each
  // subscreen drawing its own. Desktop/tablet render the original
  // continuous pill below, untouched.
  if (isMobile) {
    // Top line of the collapsed pill: category + place (or the placeholder)
    // -- date/time used to be crammed onto this same line, unreadable once
    // both a longer place name and a date range were both set. Now split
    // onto their own smaller line below (see whenDate/whenTime).
    const categoryLabel = search.category ? resolveCategoryName(search.category) : undefined;
    const summary = [categoryLabel, search.city].filter(Boolean).join(' · ') || undefined;
    const { date: whenDate, time: whenTime } = describeAvailability(search, whenRange);
    // Whether there's anything a quick clear (below) would actually clear.
    const hasAnySearch = !!search.city || !!whenRange || !!search.rentalMode || !!search.category;

    const handleQuickClear = (event: React.MouseEvent) => {
      // Not strictly needed while this sits outside the main button's own
      // DOM subtree (see the layout below), but cheap insurance against a
      // future layout change nesting it back inside.
      event.stopPropagation();
      setSearch({});
      setWhenRange(undefined);
      submitSearch({});
    };

    const { date: draftWhenDate, time: draftWhenTime } = describeAvailability(draftSearch, draftWhenRange);
    const whenSummaryText = draftWhenDate ? [draftWhenDate, draftWhenTime].filter(Boolean).join(', ') : undefined;
    const whenSummary = whenSummaryText ?? translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_WHEN_PLACEHOLDER);

    // Which quick chip (below) should actually show as active -- NOT just
    // `preset` itself: that defaults to 'now' whether or not the guest has
    // touched anything (see its own useState/resync above -- desktop wants
    // that default, since its own submit resolves it regardless of whether
    // any chip was actually tapped). Mobile's chips are a separate,
    // explicit action instead, so showing "Ahora" pre-highlighted before
    // it's genuinely been picked read as already-selected when nothing was
    // resolved into draftSearch yet. Reading draftSearch.availabilityMode
    // directly for 'now' sidesteps that default entirely; '+2h'/'+3h' don't
    // have this problem since preset never defaults to either of them.
    const activeQuickPreset: 'now' | '+2h' | '+3h' | undefined =
      draftSearch.availabilityMode === 'Now' ? 'now' : preset === '+2h' || preset === '+3h' ? preset : undefined;

    // Machinery/Operators share the Machinery.Category taxonomy;
    // Accessories searches its own separate AccessoryType taxonomy instead
    // (see resolveCategoryName's own doc comment above) -- only the section
    // title/placeholder wording, and whether the catalog's "currently
    // active" narrowing applies, differ per mode.
    const categoryLabelKey =
      mode === 'accessories'
        ? TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_CATEGORY_ACCESSORY
        : mode === 'operators'
          ? TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_CATEGORY_OPERATOR
          : mode === 'services'
            ? TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_CATEGORY_SERVICE
            : TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_CATEGORY;
    const categoryPlaceholderKey =
      mode === 'accessories'
        ? TRANSLATIONS.HOMEPAGE_SEARCH_CATEGORY_PLACEHOLDER_ACCESSORY
        : mode === 'operators'
          ? TRANSLATIONS.HOMEPAGE_SEARCH_CATEGORY_PLACEHOLDER_OPERATOR
          : mode === 'services'
            ? TRANSLATIONS.HOMEPAGE_SEARCH_CATEGORY_PLACEHOLDER_SERVICE
            : TRANSLATIONS.HOMEPAGE_SEARCH_CATEGORY_PLACEHOLDER;

    const screenTitle =
      screen === 'where'
        ? translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_WHERE)
        : screen === 'when'
        ? translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_WHEN)
        : screen === 'category'
        ? translate(t, categoryLabelKey)
        : '';

    return (
      <>
        <div style={{ position: 'relative', width: '100%' }}>
          <button
            type="button"
            onClick={openOverlay}
            className={pulseOnMount ? 'search-control' : undefined}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              // A little tighter on top/bottom than the single-line version
              // had -- the second line (when present) adds its own height,
              // so matching padding on both would grow the pill more than
              // necessary.
              padding: '12px 18px',
              borderRadius: 999,
              border: `1px solid ${token.colorBorderSecondary}`,
              background: 'transparent',
              // Soft coral-tinted shadow -- a hint of the brand accent without
              // a filled background, per this control's own "minimal" brief.
              boxShadow: `0 2px 8px rgba(216,90,48,0.15)`,
              cursor: 'pointer',
              ...style,
              // Room for the overlapping clear button (below) -- after the
              // spread, so it always wins over whatever paddingRight a
              // caller's own `style` sets (e.g. ExploreMobileMap's header
              // pill), and only reserved once there's actually a clear
              // button to make room for.
              ...(hasAnySearch ? { paddingRight: 44 } : null),
            }}
          >
            <SearchOutlined style={{ fontSize: 16, color: token.colorTextSecondary, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 15,
                  fontWeight: summary ? 600 : 400,
                  color: summary ? token.colorText : token.colorTextTertiary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {summary ?? translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_SUMMARY_PLACEHOLDER)}
              </span>
              {/* Only takes up room once there's an actual date to show --
                  a bare place with no "When" picked yet stays single-line. */}
              {whenDate && (
                <span
                  style={{
                    display: 'flex',
                    justifyContent: whenTime ? 'space-between' : 'flex-start',
                    gap: 8,
                    marginTop: 1,
                    fontSize: 12,
                    color: token.colorTextTertiary,
                    overflow: 'hidden',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{whenDate}</span>
                  {whenTime && <span style={{ flexShrink: 0 }}>{whenTime}</span>}
                </span>
              )}
            </span>
          </button>
          {hasAnySearch && (
            <button
              type="button"
              aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CLEAR_FILTERS)}
              onClick={handleQuickClear}
              style={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: 'none',
                background: token.colorFillTertiary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: token.colorTextSecondary,
              }}
            >
              <IconX size={14} />
            </button>
          )}
        </div>

        {/* Portaled straight onto document.body -- same reasoning as
            ConfirmBottomSheet: any ancestor up the call chain with a CSS
            transform (framer-motion page transitions add one) creates its
            own containing block, silently rescoping this "fixed" overlay
            and dropping its whole subtree into a nested stacking context
            where its zIndex: 2000 no longer competes with the rest of the
            page. That's exactly what let MobileBottomNav (zIndex: 1000,
            but living in a different, higher-priority stacking context)
            paint on top of this overlay's own "Buscar" button despite the
            2000 > 1000 z-index looking like it should win. */}
        {createPortal(
          <AnimatePresence>
            {overlayOpen && (
              <motion.div
                key="mobile-search-overlay"
                initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2000,
                background: token.colorBgContainer,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0', flexShrink: 0 }}>
                <div style={{ width: 36, height: 36, flexShrink: 0 }}>
                  {screen !== 'main' && (
                    <button
                      type="button"
                      aria-label={translate(t, TRANSLATIONS.BACK)}
                      onClick={() => setScreen('main')}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: 'none',
                        background: token.colorFillTertiary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <IconArrowLeft size={18} />
                    </button>
                  )}
                </div>
                <span style={{ fontSize: 16, fontWeight: 600, color: token.colorText }}>{screenTitle}</span>
                <button
                  type="button"
                  aria-label={translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_OVERLAY_CLOSE)}
                  onClick={() => setOverlayOpen(false)}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: 'none',
                    background: token.colorFillTertiary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <CloseOutlined style={{ fontSize: 16 }} />
                </button>
              </div>

              <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                {/* Main screen -- always mounted underneath, covered (not
                    unmounted) by whichever subscreen is pushed on top, so
                    its own scroll position/state survives a back-navigation. */}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 20px' }}>
                    <SearchNavRow
                      label={translate(t, categoryLabelKey)}
                      summary={(draftSearch.category ? resolveCategoryName(draftSearch.category) : undefined) || translate(t, categoryPlaceholderKey)}
                      hasSummary={!!draftSearch.category}
                      onClick={() => setScreen('category')}
                    />

                    <SearchNavRow
                      label={translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_WHERE)}
                      summary={draftSearch.city || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CITY_PLACEHOLDER)}
                      hasSummary={!!draftSearch.city}
                      onClick={() => setScreen('where')}
                    />

                    {/* Modalidad/Cuándo only mean anything for Machinery --
                        an Accessory/Operator has a single flat rate, no
                        rental modality to pick and (today) no date-based
                        availability check on the map search endpoints that
                        back them -- see this bar's own `mode` prop doc
                        comment. Dropped entirely rather than shown inert. */}
                    {mode === 'machinery' && (
                      <>
                        <div style={{ background: token.colorFillAlter, borderRadius: 20, marginBottom: 12, padding: '16px 18px' }}>
                          <span style={{ display: 'block', fontSize: 13, color: token.colorTextSecondary, marginBottom: 10 }}>
                            {translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_MODE)}
                          </span>
                          {renderModeChips(draftSearch, setDraftSearch)}
                        </div>

                        <SearchNavRow
                          label={translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_WHEN)}
                          summary={whenSummary}
                          hasSummary={!!whenSummaryText}
                          onClick={() => setScreen('when')}
                        />
                      </>
                    )}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '16px 20px',
                      paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                      borderTop: `1px solid ${token.colorBorderSecondary}`,
                      flexShrink: 0,
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleClearAll}
                      style={{
                        border: 'none',
                        background: 'none',
                        textDecoration: 'underline',
                        fontSize: 15,
                        color: token.colorText,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CLEAR_FILTERS)}
                    </button>
                    <motion.button
                      type="button"
                      onClick={handleOverlaySearch}
                      whileTap={{ scale: 0.97 }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '13px 26px',
                        borderRadius: 999,
                        border: 'none',
                        background: CORAL,
                        color: '#fff',
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <SearchOutlined style={{ fontSize: 15 }} />
                      {translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_BUTTON)}
                    </motion.button>
                  </div>
                </div>

                {/* Subscreens -- pushed in from the right, popped back out
                    to the right, same slide pattern as the app's other
                    mobile drill-in navigation. */}
                <AnimatePresence>
                  {screen !== 'main' && (
                    <motion.div
                      key={screen}
                      initial={{ x: '100%' }}
                      animate={{ x: 0 }}
                      exit={{ x: '100%' }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      style={{ position: 'absolute', inset: 0, background: token.colorBgContainer, display: 'flex', flexDirection: 'column' }}
                    >
                      {screen === 'category' && (
                        <HomeSearchCategoryScreen
                          initialQuery={draftSearch.category ? resolveCategoryName(draftSearch.category) : ''}
                          onSelect={(category) => {
                            setDraftSearch((prev) => ({ ...prev, category }));
                            setScreen('main');
                          }}
                          narrowToActive={mode === 'machinery'}
                          placeholder={mode === 'machinery' ? undefined : translate(t, categoryPlaceholderKey)}
                          taxonomySource={mode === 'accessories' ? 'accessory' : mode === 'services' ? 'service' : 'machinery'}
                        />
                      )}
                      {screen === 'where' && (
                        <HomeSearchLocationScreen
                          initialQuery={draftSearch.city ?? ''}
                          onSelect={(location) => {
                            const hasPoint = location.latitude !== 0 || location.longitude !== 0;
                            setDraftSearch((prev) => ({
                              ...prev,
                              city: location.locality || location.formattedAddress,
                              lat: hasPoint ? location.latitude : undefined,
                              lng: hasPoint ? location.longitude : undefined,
                              radiusKm: hasPoint ? LOCATION_DEFAULT_RADIUS_KM : undefined,
                            }));
                            setScreen('main');
                          }}
                          onClear={() =>
                            setDraftSearch((prev) => ({ ...prev, city: undefined, lat: undefined, lng: undefined, radiusKm: undefined }))
                          }
                          onNearMe={handleDraftNearMe}
                          locating={locating}
                        />
                      )}
                      {screen === 'when' && (
                        <HomeSearchWhenScreen
                          rentalMode={draftSearch.rentalMode}
                          onSelectionChange={handleWhenSelectionChange}
                          activePreset={activeQuickPreset}
                          onQuickAvailability={handleQuickAvailability}
                          onConfirm={() => setScreen('main')}
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </>
    );
  }

  const whereField = renderWhereField(search, setSearch, handleNearMe);
  const whenField = renderWhenField(search, setSearch, preset, setPreset, customAt, setCustomAt, pickerOpen, setPickerOpen);
  const modeField = renderModeField(search, setSearch);

  return (
    <div
      style={{
        maxWidth: 900,
        margin: '0 auto 32px',
        display: 'flex',
        flexDirection: 'row',
        // Always stretch, never 'center' -- if one section (When, once its
        // shortcuts wrap to a second line) ends up taller than the others,
        // centering each section independently would float their micro-labels
        // at different heights instead of all starting at the same padding-top.
        alignItems: 'stretch',
        borderRadius: 999,
        background: token.colorBgContainer,
        boxShadow: '0 8px 28px rgba(0,0,0,0.10)',
        border: `1px solid ${token.colorBorderSecondary}`,
        ...style,
      }}
    >
      {/* A few extra px of left padding here only -- this section sits in the
          pill's own rounded corner, so the same 20px padding every other
          section uses reads as tighter against the curve than it actually is. */}
      <div style={{ ...sectionStyle(false), paddingLeft: 28 }}>
        <span style={microLabelStyle}>{translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_WHERE)}</span>
        {whereField}
      </div>

      <div style={{ ...sectionStyle(false), flex: 1.3 }}>
        <span style={microLabelStyle}>{translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_WHEN)}</span>
        {whenField}
      </div>

      <div style={sectionStyle(true)}>
        <span style={microLabelStyle}>{translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_LABEL_MODE)}</span>
        {modeField}
      </div>

      <div
        style={{
          padding: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <motion.button
          type="button"
          aria-label={translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_BUTTON)}
          onClick={handleSearch}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: 'none',
            background: token.colorPrimary,
            color: token.colorTextLightSolid,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <SearchOutlined style={{ fontSize: 18 }} />
        </motion.button>
      </div>
    </div>
  );
};

export default HomeSearchBar;
